import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { imgUrl } from "@/context/CartContext";

const STATUSES = ["active", "draft", "archived"];
const ORDER_STATUSES = ["new", "processing", "ready", "fulfilled", "cancelled"];
const EMPTY = {
  name: "", short_description: "", description: "", usage_instructions: "",
  price: 0, sale_price: "", category: "", sku: "",
  track_inventory: false, stock_qty: 0, in_stock: true,
  images: [], featured: false, display_order: 0, status: "active",
};

export default function ClinicShop() {
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [form, setForm] = useState(null);
  const [msg, setMsg] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const [p, o] = await Promise.all([api.get("/shop/admin/products"), api.get("/shop/admin/orders")]);
      setProducts(p.data.products || []);
      setOrders(o.data.orders || []);
    } catch { setMsg("Failed to load shop data"); }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setMsg("");
    const payload = { ...form, price: Number(form.price) || 0, sale_price: form.sale_price === "" ? null : Number(form.sale_price), stock_qty: Number(form.stock_qty) || 0, display_order: Number(form.display_order) || 0 };
    try {
      if (form.id) await api.put(`/shop/admin/products/${form.id}`, payload);
      else await api.post("/shop/admin/products", payload);
      setForm(null); await load(); setMsg("Product saved.");
    } catch (e) { setMsg(e?.response?.data?.detail || "Save failed"); }
  };

  const archive = async (id) => {
    if (!window.confirm("Archive this product? It will be hidden from the shop.")) return;
    try { await api.delete(`/shop/admin/products/${id}`); await load(); } catch { setMsg("Archive failed"); }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setMsg("");
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/shop/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("images", [...(form.images || []), data.url]);
    } catch (e) { setMsg(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeImage = (u) => set("images", (form.images || []).filter((x) => x !== u));

  const setOrderStatus = async (id, order_status) => {
    try { await api.patch(`/shop/admin/orders/${id}`, { order_status }); await load(); } catch { setMsg("Update failed"); }
  };

  const input = "mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm";

  return (
    <div className="min-h-screen bg-[#FBF8F2]">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between">
          <span className="font-display text-lg text-slate-900">KISSED Clinic OS · Shop</span>
          <Link to="/admin" className="text-xs text-purple-900 hover:underline">← Admin Hub</Link>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-6 py-8" data-testid="clinic-shop-admin">
        {msg && <div className="mb-4 text-sm text-purple-900 bg-purple-50 rounded-lg px-3 py-2" data-testid="shop-msg">{msg}</div>}

        {!form && (
          <div className="flex gap-2 mb-6">
            <button onClick={() => setTab("products")} className={`text-sm rounded-full px-4 py-1.5 ${tab === "products" ? "bg-purple-900 text-white" : "bg-white border border-slate-300 text-slate-600"}`} data-testid="tab-products">Products</button>
            <button onClick={() => setTab("orders")} className={`text-sm rounded-full px-4 py-1.5 ${tab === "orders" ? "bg-purple-900 text-white" : "bg-white border border-slate-300 text-slate-600"}`} data-testid="tab-orders">Orders ({orders.length})</button>
          </div>
        )}

        {/* PRODUCTS */}
        {!form && tab === "products" && (
          <>
            <div className="flex items-center justify-between mb-4">
              <h1 className="font-display text-2xl text-slate-900">Products</h1>
              <button onClick={() => setForm({ ...EMPTY })} className="rounded-full bg-purple-900 text-white text-sm px-4 py-2" data-testid="shop-new-product">+ New Product</button>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 divide-y">
              {products.map((p) => (
                <div key={p.id} className="p-4 flex items-center gap-4" data-testid={`product-row-${p.id}`}>
                  <div className="h-12 w-12 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
                    {p.images?.[0] ? <img src={imgUrl(p.images[0])} alt="" className="w-full h-full object-cover" /> : null}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 truncate">{p.name} {p.featured && <span className="text-[10px] uppercase text-green-700 bg-green-50 rounded px-1.5 py-0.5 ml-1">Featured</span>}</div>
                    <div className="text-xs text-slate-500">${Number(p.price).toFixed(2)}{p.sale_price ? ` · sale $${Number(p.sale_price).toFixed(2)}` : ""} · {p.category || "uncategorized"} · {p.status}{p.track_inventory ? ` · ${p.stock_qty} in stock` : ""}</div>
                  </div>
                  <button onClick={() => setForm({ ...p, sale_price: p.sale_price ?? "" })} className="text-xs text-purple-900 hover:underline" data-testid={`product-edit-${p.id}`}>Edit</button>
                  <button onClick={() => archive(p.id)} className="text-xs text-red-600 hover:underline" data-testid={`product-archive-${p.id}`}>Archive</button>
                </div>
              ))}
              {products.length === 0 && <div className="p-6 text-sm text-slate-500">No products yet.</div>}
            </div>
          </>
        )}

        {/* ORDERS */}
        {!form && tab === "orders" && (
          <div className="bg-white rounded-xl border border-slate-200 divide-y" data-testid="orders-list">
            {orders.map((o) => (
              <div key={o.id} className="p-4" data-testid={`order-row-${o.id}`}>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="font-medium text-slate-900">{o.order_number}</div>
                  <span className={`text-[10px] uppercase rounded px-1.5 py-0.5 ${o.payment_status === "paid" ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`}>{o.payment_status}</span>
                  <div className="text-xs text-slate-500">{new Date(o.created_at).toLocaleString()}</div>
                  <div className="ml-auto font-semibold text-slate-900">${Number(o.total).toFixed(2)}</div>
                </div>
                <div className="mt-1 text-xs text-slate-600">{o.customer_name || "—"} · {o.email || "no email"} · {(o.items || []).map((i) => `${i.name}×${i.quantity}`).join(", ")}</div>
                <div className="mt-2">
                  <select value={o.order_status} onChange={(e) => setOrderStatus(o.id, e.target.value)} className="text-xs border border-slate-300 rounded px-2 py-1" data-testid={`order-status-${o.id}`}>
                    {ORDER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            ))}
            {orders.length === 0 && <div className="p-6 text-sm text-slate-500">No orders yet.</div>}
          </div>
        )}

        {/* PRODUCT FORM */}
        {form && (
          <div className="bg-white rounded-xl border border-slate-200 p-6 max-w-2xl">
            <h1 className="font-display text-xl text-slate-900 mb-4">{form.id ? "Edit Product" : "New Product"}</h1>
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="text-sm sm:col-span-2">Name<input className={input} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="product-name" /></label>
              <label className="text-sm sm:col-span-2">Short description<input className={input} value={form.short_description} onChange={(e) => set("short_description", e.target.value)} /></label>
              <label className="text-sm sm:col-span-2">Full description<textarea className={input} rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} /></label>
              <label className="text-sm sm:col-span-2">Usage / instructions (optional)<textarea className={input} rows={2} value={form.usage_instructions} onChange={(e) => set("usage_instructions", e.target.value)} /></label>
              <label className="text-sm">Price (USD)<input type="number" step="0.01" className={input} value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="product-price" /></label>
              <label className="text-sm">Sale price (optional)<input type="number" step="0.01" className={input} value={form.sale_price} onChange={(e) => set("sale_price", e.target.value)} /></label>
              <label className="text-sm">Category<input className={input} value={form.category} onChange={(e) => set("category", e.target.value)} placeholder="Herbal Formulas" /></label>
              <label className="text-sm">SKU (optional)<input className={input} value={form.sku} onChange={(e) => set("sku", e.target.value)} /></label>
              <label className="text-sm">Display order<input type="number" className={input} value={form.display_order} onChange={(e) => set("display_order", e.target.value)} /></label>
              <label className="text-sm">Status<select className={input} value={form.status} onChange={(e) => set("status", e.target.value)}>{STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</select></label>
              <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={form.track_inventory} onChange={(e) => set("track_inventory", e.target.checked)} /> Track inventory</label>
              {form.track_inventory
                ? <label className="text-sm">Stock quantity<input type="number" className={input} value={form.stock_qty} onChange={(e) => set("stock_qty", e.target.value)} /></label>
                : <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={form.in_stock} onChange={(e) => set("in_stock", e.target.checked)} /> In stock</label>}
              <label className="text-sm flex items-center gap-2 sm:col-span-2"><input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} /> Featured on homepage</label>

              <div className="text-sm sm:col-span-2">
                <div className="mb-2">Product images</div>
                <div className="flex flex-wrap gap-3 items-center">
                  {(form.images || []).map((u) => (
                    <div key={u} className="relative h-20 w-20 rounded-lg overflow-hidden border border-slate-200">
                      <img src={imgUrl(u)} alt="" className="w-full h-full object-cover" />
                      <button onClick={() => removeImage(u)} className="absolute top-0.5 right-0.5 bg-white/90 rounded-full h-5 w-5 text-xs text-red-600">×</button>
                    </div>
                  ))}
                  <button onClick={() => fileRef.current?.click()} disabled={uploading} className="h-20 w-20 rounded-lg border-2 border-dashed border-slate-300 text-xs text-slate-500" data-testid="product-upload-btn">{uploading ? "…" : "+ Image"}</button>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadImage} data-testid="product-upload-input" />
                </div>
              </div>
            </div>
            <div className="mt-6 flex gap-2">
              <button onClick={save} className="rounded-full bg-purple-900 text-white text-sm px-5 py-2" data-testid="product-save">Save</button>
              <button onClick={() => setForm(null)} className="rounded-full border border-slate-300 text-sm px-5 py-2">Cancel</button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
